import type { Client, Devis, FactureDevis, LigneDevis } from '../../db/modele'
import { acheteurFacture } from '../clients'
import { acomptesDeduits } from '../acomptes'
import type { Entreprise } from '../entreprise'
import { totalDocument } from '../money'
import { versCalcul } from '../pdf/devis-pdf'
import { totauxEn16931, type Facture } from './cii'
import { DELAI_REGLEMENT_JOURS, type ResultatFactureSituation } from './situation'
import { reglagesFacture } from './reglages'

// Facture directe d'un devis signé (hors situations) : mêmes lignes que le devis, numéro `FAC-AAAA-NNNN`
// attribué par le serveur. Son avoir total reprend les mêmes lignes (type 381, `AVO-AAAA-NNNN`).

const jourIso = (d: Date) => d.toISOString().slice(0, 10)

export type ResultatFactureDevis = ResultatFactureSituation

/**
 * Facture (ou, avec `avoir`, avoir total) d'un devis signé. Renvoie ce qui manque tant que l'émission est
 * impossible : réglages de l'entreprise, fiche client, et en franchise de TVA un devis sans TVA (un devis émis
 * est immuable : il faut en tirer une copie à 0 %, il n'est pas modifié ici).
 */
export function factureDeDevis(
  devis: Pick<Devis, 'numero' | 'lignes' | 'factures' | 'autoliquidation'>,
  facture: Pick<FactureDevis, 'numero' | 'emisLe' | 'acomptePercent' | 'lignes'>,
  client: Client,
  entreprise: Entreprise,
  avoir?: { numero: string; emisLe: string },
): ResultatFactureDevis {
  const manques: string[] = []
  if (!facture.numero) manques.push('le numéro de la facture')
  if (avoir && !avoir.numero) manques.push('le numéro de l’avoir')
  const vendeur = reglagesFacture(entreprise)
  if (!vendeur.ok) manques.push(...vendeur.manques.map((m) => `${m} de l’entreprise (réglages)`))
  const acheteur = acheteurFacture(client)
  if (!acheteur.ok) manques.push(...acheteur.manques.map((m) => `${m} du client`))
  if (entreprise.franchiseTva && devis.lignes.some((l) => l.tvaPercent !== 0)) {
    manques.push('un devis à 0 % de TVA (votre entreprise est en franchise : créez une copie à 0 % du devis)')
  }
  if (devis.autoliquidation) {
    if (entreprise.franchiseTva) manques.push('une entreprise assujettie à la TVA (l’autoliquidation ne se combine pas avec la franchise en base)')
    if (devis.lignes.some((l) => l.tvaPercent !== 0)) manques.push('un devis dont toutes les lignes sont à 0 % de TVA (autoliquidation)')
  }
  if (manques.length || !vendeur.ok || !acheteur.ok) return { ok: false, manques }

  const dateFacture = new Date(facture.emisLe)
  const date = avoir ? new Date(avoir.emisLe) : dateFacture
  const echeance = avoir ? date : new Date(date.getTime() + DELAI_REGLEMENT_JOURS * 86_400_000)
  // Facture d'acompte : les lignes fixées par le serveur ; sinon celles du devis, moins les acomptes déjà facturés
  const acompte = facture.acomptePercent !== undefined
  const lignes: LigneDevis[] = (acompte ? (facture.lignes ?? []) : devis.lignes).map((l) => ({ ...l }))
  if (!lignes.length) return { ok: false, manques: ['les lignes de la facture d’acompte'] }
  const deduits = acompte ? [] : acomptesDeduits(devis, facture)
  const acomptesDeduitsXml = deduits.map((a) => ({
    numeroFacture: a.numero,
    dateFacture: jourIso(new Date(a.emisLe)),
    ttcCents: totauxEn16931((a.lignes ?? []).map((l) => (entreprise.franchiseTva ? { ...l, tvaPercent: 0 } : l))).ttc.times(100).toNumber(),
  }))
  const f: Facture = {
    numero: avoir?.numero ?? facture.numero,
    date: jourIso(date),
    echeance: jourIso(echeance),
    acheteur: acheteur.acheteur,
    lignes,
    contrat: devis.numero,
    ...(avoir && { avoir: { numeroFacture: facture.numero, dateFacture: jourIso(dateFacture) } }),
    ...(acompte && !avoir && { acompte: true }),
    ...(acomptesDeduitsXml.length && { acomptesDeduits: acomptesDeduitsXml }),
    ...(devis.autoliquidation && { autoliquidation: true }),
    notes: [
      ...(avoir ? [`Avoir annulant la facture ${facture.numero} du ${jourIso(dateFacture).split('-').reverse().join('/')}.`] : []),
      acompte ? `Facture d’acompte de ${String(facture.acomptePercent).replace('.', ',')} % sur le devis ${devis.numero}.` : `Facture sur le devis ${devis.numero}.`,
      ...(deduits.length ? [`Acomptes déjà facturés déduits : ${deduits.map((a) => a.numero).join(', ')}.`] : []),
    ],
    ...vendeur.reglages,
  }
  // La norme calcule la TVA par catégorie sur sa base : écart possible de quelques centimes avec l'écran et le PDF
  const ttcEcran = totalDocument(lignes.map(versCalcul)).ttc
  const ecartTtcCents = totauxEn16931(f.franchise ? lignes.map((l) => ({ ...l, tvaPercent: 0 })) : lignes).ttc.minus(ttcEcran).times(100).toNumber()
  return { ok: true, facture: f, ecartTtcCents }
}
